import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'src/app/private/services/api.service';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

export interface LeadActivityLogPayload {
  lead_id: number | string;
  activity_type: string;
  activity_description: string;
  related_table?: string;
  related_id?: number | string;
  status?: string;
  user_id?: number | string;
  client_id?: number | string;
  center_id?: number | string;
}

@Injectable({
  providedIn: 'root',
})
export class LeadActivityService {
  constructor(
    private api: ApiService,
    private localStorage: LocalStorageService
  ) {}

  private basePayload(leadId: number | string): Partial<LeadActivityLogPayload> {
    return {
      client_id: this.localStorage.getClientId(),
      center_id: this.localStorage.getCenterId(),
      lead_id: leadId,
      user_id: this.localStorage.getUserId(),
    };
  }

  createLeadActivityLog(data: LeadActivityLogPayload): Observable<any> {
    return this.api.post(environment.createLeadActivityLog, {
      ...this.basePayload(data.lead_id),
      ...data,
    });
  }

  /** Fetch all activity rows for a lead (not scoped to current user). */
  getAllLeadActivity(leadId: number | string, page = 0, limit = 100): Observable<any> {
    return this.api.post(environment.getAllLeadActivity, {
      client_id: this.localStorage.getClientId(),
      center_id: this.localStorage.getCenterId(),
      lead_id: leadId,
      page,
      limit,
    });
  }

  backfillLeadActivity(leadId: number | string, force = false): Observable<any> {
    return this.api.post(environment.backfillLeadActivity, {
      ...this.basePayload(leadId),
      force,
    });
  }

  record(
    leadId: number | string,
    activityType: string,
    description: string,
    opts: Partial<LeadActivityLogPayload> = {}
  ): Observable<unknown> {
    return this.createLeadActivityLog({
      lead_id: leadId,
      activity_type: activityType,
      activity_description: description,
      ...opts,
    });
  }

  recordNoteCreated(leadId: number | string, noteId: number, text: string): Observable<unknown> {
    return this.record(leadId, 'lead_notes_activity', `Note added: '${text.slice(0, 200)}'`, {
      related_table: 'notes',
      related_id: noteId,
    });
  }

  recordTaskCreated(
    leadId: number | string,
    taskId: number,
    title: string,
    status?: string
  ): Observable<unknown> {
    return this.record(leadId, 'lead_tasks_activity', `Task added: '${title.slice(0, 200)}'`, {
      related_table: 'tasks',
      related_id: taskId,
      status,
    });
  }

  recordCommunication(
    leadId: number | string,
    logId: number,
    type: string,
    note: string,
    status?: string
  ): Observable<unknown> {
    return this.record(
      leadId,
      'lead_communication_activity',
      `${type} logged: ${(note || '').slice(0, 200)}`,
      {
        related_table: 'communication_history_log',
        related_id: logId,
        status,
      }
    );
  }

  recordFollowUp(leadId: number | string, historyId: number, remarks: string): Observable<unknown> {
    return this.record(leadId, 'lead_followup_activity', `Follow-up: ${remarks.slice(0, 200)}`, {
      related_table: 'leadhistoryrecord',
      related_id: historyId,
    });
  }

  recordLeadFieldChange(
    leadId: number | string,
    fieldLabel: string,
    fromVal: unknown,
    toVal: unknown
  ): Observable<unknown> {
    return this.record(
      leadId,
      'lead_activity',
      `${fieldLabel} changed from ${fromVal ?? '—'} to ${toVal ?? '—'}`
    );
  }

  recordLeadDetailsUpdated(leadId: number | string): Observable<unknown> {
    return this.record(leadId, 'lead_activity', 'Lead details updated');
  }

  /**
   * Logs a pipeline status change. Omits related_table/related_id so each update
   * is its own row (the leadsrecord backfill row uses that unique key already).
   */
  recordLeadStatusChange(
    leadId: number | string,
    fromStatus: string | null | undefined,
    toStatus: string
  ): Observable<unknown> {
    const fromLabel = fromStatus?.trim() || '—';
    const toLabel = toStatus?.trim() || '—';
    return this.createLeadActivityLog({
      lead_id: leadId,
      activity_type: 'lead_activity',
      activity_description: `Lead status changed from ${fromLabel} to ${toLabel}`,
      status: toLabel,
    });
  }

}
